import React, { useEffect, useState } from 'react';
import { ReactComponent as BackArrow } from 'assets/icons/back_arrow.svg';
import { ReactComponent as ExportIcon } from 'assets/icons/export_icon.svg';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import RoleTag from 'components/base/role-tag';
import { ENUM_INBOUND_ROLE } from 'utils/enum';

import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  ToastMessage,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import moment from 'moment';
import { apiService } from 'utils/api';
import { UserType } from 'models/user.model';
import Issues from 'components/cs/reps-details/issues';
import TripHistory from 'components/cs/reps-details/trip-history';
import { toast } from 'react-toastify';
import ExportModal from 'components/cs/modals/export-modal';
import { getUserName } from 'utils/functions';

const ALL_ISSUES_TABS = {
  ALL: 'All',
  RESOLVED: 'Resolved',
  UNRESOLVED: 'Unresolved'
};
type OutboundFilterType = (typeof ALL_ISSUES_TABS)[keyof typeof ALL_ISSUES_TABS];
const ALL_TRIP_TABS = {
  ALL: 'All',
  COMPLETED_TRIPS: 'Completed Trips',
  FAILED_TRIPS: 'Failed Trips',
  RETURN_TRIPS: 'Return Trips'
} as const;

type InboundFilterType = (typeof ALL_TRIP_TABS)[keyof typeof ALL_TRIP_TABS];

const RepDetails = () => {
  const [searchParams] = useSearchParams();
  const { id } = useParams();

  const role = searchParams.get('role') as ENUM_INBOUND_ROLE;
  const [issuesActiveTab, setIssuesActiveTab] = useState<OutboundFilterType>(ALL_ISSUES_TABS.ALL);
  const [tripActiveTab, setTripActiveTab] = useState<InboundFilterType>(ALL_TRIP_TABS.ALL);
  const [filter, setFilter] = useState(getDateRangeSelector(ENUM_DATE_RANGE.THIS_WEEK));
  const [repDetails, setRepDetails] = useState<UserType>();
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    getRepDetails();
  }, []);
  const getRepDetails = async () => {
    try {
      const res = await apiService.get(`cs/staff/one/${id}`, {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setRepDetails(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };
  const exportIssues = async (
    email: string,
    dateRange: {
      startDate: Date;
      endDate: Date;
    }
  ) => {
    const { startDate: start_date, endDate: end_date } = dateRange;
    const issuesTab =
      issuesActiveTab === 'Unresolved'
        ? 'pending'
        : issuesActiveTab === 'Resolved'
        ? 'resolved'
        : 'all';
    const tripTab =
      tripActiveTab === ALL_TRIP_TABS.COMPLETED_TRIPS
        ? 'completed'
        : tripActiveTab === ALL_TRIP_TABS.FAILED_TRIPS
        ? 'failed'
        : tripActiveTab === ALL_TRIP_TABS.RETURN_TRIPS
        ? 'return'
        : 'all';
    const url = role.includes('inbound')
      ? `cs/assigned/${id}/issue/export/csv/${issuesTab}`
      : `cs/assigned/${id}/order/export/csv/${tripTab}`;
    try {
      await apiService.get(url, {
        params: {
          email,
          start_date,
          end_date
        }
      });
      toast(<ToastMessage type='success' message='Data exported successfully' />);
      setShowExportModal(false);
    } catch {
      toast(<ToastMessage type='error' message='Failed to export data' />);
    }
  };
  return (
    <div>
      <div className='flex justify-between'>
        <div className='flex items-center gap-4'>
          <Link to='/cs/settings'>
            <div className='w-[30px] h-[30px] bg-[#F4F1F1] rounded-full flex justify-center items-center cursor-pointer'>
              <BackArrow className='w-4 h-4' />
            </div>
          </Link>
          <div>
            <div className='flex gap-2'>
              <p className='font-medium'>{getUserName(repDetails)}</p>
              <RoleTag type={role} />
            </div>
            <p className='text-xs text-gray-5'>
              Added {moment(repDetails?.created_at?.split('T')[0]).format('LL')}
            </p>
          </div>
        </div>
        <div className='flex gap-4 items-center'>
          <div
            className='w-[137px] h-[36px] bg-[#F2F2F2] flex items-center justify-center rounded-[100px] cursor-pointer'
            onClick={() => setShowExportModal(true)}>
            <ExportIcon className='w-4 h-4' />
            <p className='text-SM text-[2E3031] ml-2 font-medium'>Export</p>
          </div>
          <DateRangeSelector showTime onChange={(v) => setFilter(v.date)} />
        </div>
      </div>
      {role === ENUM_INBOUND_ROLE.CS_INBOUND ? (
        <Issues filter={filter} activeTab={issuesActiveTab} setActiveTab={setIssuesActiveTab} />
      ) : (
        <TripHistory filter={filter} activeTab={tripActiveTab} setActiveTab={setTripActiveTab} />
      )}
      <ExportModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        title={`Export ${role?.includes('outbound') ? 'Issues' : 'Trip History'}`}
        onSubmit={(
          email: string,
          dateRange: {
            startDate: Date;
            endDate: Date;
          }
        ) => exportIssues(email, dateRange)}
      />
    </div>
  );
};

export default RepDetails;
